import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";

/** `note`, in the dsh register — the ambient line, and the space between
 * turns.
 *
 * Two faces and no others, which is the contract's own split: `text` for
 * session weather and `rule` for a separator.
 *
 * ## The live line shimmers and nothing else about it changes
 *
 * The native skin steps an active note UP the ramp — `--p-size` instead of
 * `--small-size` — because in a document the live line is the one thing
 * actually happening and it earns the reading size. dsh does not, and the
 * difference is the surface again: its transcript is a narrow column where
 * every ambient line sits at one size, and a line that grows when it goes
 * live makes the column jump at the exact moment the reader is watching it.
 * So the size is constant and the LIGHT is the whole signal.
 *
 * The treatment is the gradient this repo already ships in the native skin:
 * a five-stop ramp painted behind the line and clipped to the glyphs, swept
 * across at 1.8s. Both ends of the ramp are the SAME tone, so the line at
 * rest is a plain dim line and only the crest is bright — a two-stop
 * gradient pulses the whole line, which reads as a broken element rather
 * than as a moving one.
 *
 * No mark beside it. The native skin puts `std-state-dot` in the gutter of a
 * live note; here the shimmer is already saying it, and a dot in a gutter
 * this skin otherwise reserves for row KINDS would read as a row that lost
 * its glyph.
 *
 * ## The rule is a gap, not a line
 *
 * A bare `rule` draws 16px of nothing. That is the whole face. A hairline
 * across a transcript is a section break, and the thing between two turns is
 * not a section break — it is a breath, and prior art (both vendor surfaces)
 * spends space on it rather than ink. A LABELLED rule is a different claim
 * ("retry", "final message"), and a label floating in space with nothing to
 * anchor it reads as a stray word, so that one gets its hairlines. */
@component({ name: "std-dsh-turn-note" })
export class DshTurnNote extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");
  private $label: HTMLElement = this.$("label");

  static makeWith(props: DshTurnNote.Props): DshTurnNote {
    return (DshTurnNote.make() as DshTurnNote).bind(props);
  }

  /** Both faces write both fields on every bind. A pooled note arriving from
   * a live text line and rendering a rule must not keep the old text in a
   * node the next CSS revision makes visible again — which is exactly how a
   * hidden node becomes a bug six months later. */
  bind(props: DshTurnNote.Props): this {
    this.setAttribute("face", props.face);
    if (props.face === "rule") {
      const label = props.label ?? "";
      this.$label.textContent = label;
      this.$text.textContent = "";
      this.toggleAttribute("active", false);
      // Closes the row's gap and drops the hairlines when there is nothing
      // to sit between them.
      this.toggleAttribute("labelled", label !== "");
      return this;
    }
    this.$label.textContent = "";
    this.toggleAttribute("labelled", false);
    this.toggleAttribute("active", props.active ?? false);
    this.$text.textContent = props.text;
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        /* The rule face claims its column: a shrink-to-fit parent would
           otherwise collapse it to the width of its label. */
        width: 100%;
      }
      #root {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .rule,
      #label {
        display: none;
      }

      /* ---- text: one dim line, live or settled -------------------- */
      #text {
        min-width: 0;
        margin: 0;
        --font-size: var(--small-size);
        color: var(--text-ter, #8b93a7);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      :host([face="rule"]) #text {
        display: none;
      }

      /* ---- text[active]: the shimmer ------------------------------ */
      /* Painted, then clipped to the glyphs. The size does not change and
         neither does the weight — the only thing that moves is the light,
         which is the property a column of these needs. */
      :host([face="text"][active]) #text {
        background-image: linear-gradient(
          100deg,
          var(--text-ter, #8b93a7) 0%,
          var(--text-ter, #8b93a7) 38%,
          var(--text-pri, #1f2328) 50%,
          var(--text-ter, #8b93a7) 62%,
          var(--text-ter, #8b93a7) 100%
        );
        background-size: 300% 100%;
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
        -webkit-text-fill-color: transparent;
        animation: dsh-note-sweep 1.8s linear infinite;
      }
      @keyframes dsh-note-sweep {
        from {
          background-position: 150% 0;
        }
        to {
          background-position: -50% 0;
        }
      }
      /* Motion is the whole signal, so what replaces it has to carry the
         same fact: the live line takes the bright tone outright rather than
         falling back to the dim one every settled note already wears. */
      @media (prefers-reduced-motion: reduce) {
        :host([face="text"][active]) #text {
          animation: none;
          background-image: none;
          color: var(--text-sec, #6a7186);
          -webkit-text-fill-color: var(--text-sec, #6a7186);
        }
      }

      /* ---- rule: a breath, and only sometimes a line -------------- */
      :host([face="rule"]) #root {
        min-height: 16px;
      }
      :host([face="rule"]:not([labelled])) #root {
        gap: 0;
      }
      :host([face="rule"][labelled]) .rule {
        display: block;
        flex: 1;
        height: 1px;
        background: var(--divider, rgba(127, 127, 127, 0.28));
        opacity: 0.7;
      }
      :host([face="rule"][labelled]) #label {
        display: block;
        flex: none;
        margin: 0;
        --font-size: var(--small-size);
        color: var(--text-ter, #8b93a7);
        white-space: nowrap;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <span class="rule"></span>
      <p class="small" id="label"></p>
      <p class="small" id="text"></p>
      <span class="rule"></span>
    </div>
  `;
}

export namespace DshTurnNote {
  export type Props =
    | {
      face: "text";
      text: string;
      /** True while the thing described is still happening: the line takes
       * the shimmer. False for a notice about something already settled. */
      active?: boolean;
    }
    | { face: "rule"; label?: string };
}
